import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { fetchProducts } from '../../api/products'
import type { ProductCard as ProductCardType } from '../../types'
import ProductCard from './ProductCard'
import { useLocalePath } from '../layout/LanguageRoute'

interface ProductRelatedProps {
  series: string
  category: string
  currentSlug: string
  /** Related products returned by the product detail API */
  serverRelated?: ProductCardType[]
}

/** Fisher-Yates shuffle (returns new array) */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export default function ProductRelated({ series, category, currentSlug, serverRelated }: ProductRelatedProps) {
  const lp = useLocalePath()
  const { t } = useTranslation()

  // Fetch from listing API as fallback if server didn't provide related
  const { data } = useQuery({
    queryKey: ['products', 'related', series, category],
    queryFn: () =>
      fetchProducts({
        series: series || undefined,
        category: series ? undefined : category || undefined,
        page_size: 20,
      }),
    enabled: !serverRelated?.length && !!(series || category),
  })

  // Combine sources, filter for images, exclude current, randomize
  const products = useMemo(() => {
    const pool = serverRelated?.length ? serverRelated : (data?.data ?? [])
    const filtered = pool.filter(
      p => p.slug !== currentSlug && p.primary_image_url
    )
    return shuffle(filtered).slice(0, 4)
  }, [serverRelated, data, currentSlug])

  if (products.length === 0) return null

  return (
    <div className="mt-12 pt-8 border-t border-neutral-200">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-neutral-900">
          {t('shop.product.moreFrom', { name: series || category })}
        </h2>
        <Link
          to={lp(`/products?${series ? `series=${encodeURIComponent(series)}` : `category=${encodeURIComponent(category)}`}`)}
          className="text-sm text-primary hover:underline"
        >
          {t('shop.product.viewAll')}
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {products.map(product => (
          <ProductCard key={product.uuid} product={product} />
        ))}
      </div>
    </div>
  )
}
